/**
 * Basic Shell - DuckDB WASM REPL Component (Fallback)
 *
 * A simple SQL REPL interface using DuckDB WASM.
 * No xterm dependency - uses plain HTML elements.
 * This is the fallback shell when xterm shell is not available.
 */

import * as duckdb from '@duckdb/duckdb-wasm';

// Production R2 bucket URL for WASM files
const R2_WASM_BASE_URL = 'https://experiment-hosting-storage.orndorff.dev/data-notebook/';

/**
 * Get the base URL for loading DuckDB assets.
 * In dev mode: uses local /wasm/ directory
 * In production: uses R2 bucket for WASM files (too large for Pages)
 */
function getAssetBaseUrl(): string {
  // Check if we're in Vite dev mode (import.meta.url points to src/)
  if (typeof import.meta !== 'undefined' && import.meta.url) {
    const url = import.meta.url;
    // In dev mode, Vite serves source files from /src/
    // WASM files are copied to /wasm/ by vite-plugin-static-copy
    if (url.includes('/src/')) {
      const origin = new URL(url).origin;
      return origin + '/wasm/';
    }
  }

  // Production mode: use R2 bucket for WASM files
  // (WASM files are too large for Cloudflare Pages 25MB limit)
  return R2_WASM_BASE_URL;
}

export interface BasicShellInstance {
  /** Whether the shell is initialized */
  isInitialized: boolean;
  /** Initialize the shell in the container */
  initialize(container: HTMLElement): Promise<void>;
  /** Get the DuckDB database instance */
  getDatabase(): duckdb.AsyncDuckDB | null;
  /** Execute a SQL query */
  executeQuery(sql: string): Promise<QueryResult>;
  /** Destroy the shell and cleanup resources */
  destroy(): Promise<void>;
}

export interface QueryResult {
  columns: string[];
  rows: any[][];
  rowCount: number;
  error?: string;
}

/**
 * Create a basic DuckDB WASM shell instance (HTML-based fallback)
 */
export function createBasicShell(): BasicShellInstance {
  let db: duckdb.AsyncDuckDB | null = null;
  let conn: duckdb.AsyncDuckDBConnection | null = null;
  let worker: Worker | null = null;
  let initialized = false;
  let container: HTMLElement | null = null;
  let historyIndex = -1;
  const commandHistory: string[] = [];

  function renderUI(containerEl: HTMLElement): void {
    container = containerEl;
    containerEl.innerHTML = `
      <div class="dn-repl">
        <div class="dn-repl-output" id="dn-repl-output">
          <div class="dn-repl-welcome">
            <div class="dn-repl-logo">DuckDB</div>
            <div class="dn-repl-version">WebAssembly Edition</div>
            <div class="dn-repl-hint">Type SQL queries and press Enter to execute</div>
          </div>
        </div>
        <div class="dn-repl-input-row">
          <span class="dn-repl-prompt">D&gt;</span>
          <input
            type="text"
            class="dn-repl-input"
            id="dn-repl-input"
            placeholder="SELECT 'Hello, DuckDB!';"
            spellcheck="false"
            autocomplete="off"
          />
        </div>
      </div>
    `;

    // Bind input events
    const input = containerEl.querySelector('#dn-repl-input') as HTMLInputElement;
    if (input) {
      input.addEventListener('keydown', handleKeyDown);
      input.focus();
    }
  }

  async function handleKeyDown(e: KeyboardEvent): Promise<void> {
    const input = e.target as HTMLInputElement;

    if (e.key === 'Enter' && input.value.trim()) {
      e.preventDefault();
      const sql = input.value.trim();

      // Add to history
      commandHistory.push(sql);
      historyIndex = commandHistory.length;

      // Show the command in output
      appendOutput(`D> ${sql}`, 'command');

      // Clear input
      input.value = '';

      // Execute query
      try {
        const result = await executeQuery(sql);
        if (result.error) {
          appendOutput(result.error, 'error');
        } else if (result.columns.length > 0) {
          appendTable(result);
        } else {
          appendOutput('Query executed successfully.', 'success');
        }
      } catch (err: any) {
        appendOutput(err.message || 'Unknown error', 'error');
      }

      // Scroll to bottom
      const output = container?.querySelector('#dn-repl-output');
      if (output) {
        output.scrollTop = output.scrollHeight;
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex > 0) {
        historyIndex--;
        input.value = commandHistory[historyIndex] || '';
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        historyIndex++;
        input.value = commandHistory[historyIndex] || '';
      } else {
        historyIndex = commandHistory.length;
        input.value = '';
      }
    }
  }

  function appendOutput(text: string, type: 'command' | 'result' | 'error' | 'success' = 'result'): void {
    const output = container?.querySelector('#dn-repl-output');
    if (!output) return;

    const line = document.createElement('div');
    line.className = `dn-repl-line dn-repl-${type}`;
    line.textContent = text;
    output.appendChild(line);
  }

  function appendTable(result: QueryResult): void {
    const output = container?.querySelector('#dn-repl-output');
    if (!output) return;

    const tableWrapper = document.createElement('div');
    tableWrapper.className = 'dn-repl-table-wrapper';

    const table = document.createElement('table');
    table.className = 'dn-repl-table';

    // Header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    for (const col of result.columns) {
      const th = document.createElement('th');
      th.textContent = col;
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = document.createElement('tbody');
    for (const row of result.rows) {
      const tr = document.createElement('tr');
      for (const cell of row) {
        const td = document.createElement('td');
        td.textContent = cell === null ? 'NULL' : String(cell);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);

    tableWrapper.appendChild(table);

    // Row count
    const rowCount = document.createElement('div');
    rowCount.className = 'dn-repl-row-count';
    rowCount.textContent = `${result.rowCount} row${result.rowCount !== 1 ? 's' : ''}`;
    tableWrapper.appendChild(rowCount);

    output.appendChild(tableWrapper);
  }

  async function executeQuery(sql: string): Promise<QueryResult> {
    if (!conn) {
      throw new Error('Database not connected');
    }

    try {
      const result = await conn.query(sql);
      const columns = result.schema.fields.map(f => f.name);
      const rows: any[][] = [];

      for (let i = 0; i < result.numRows; i++) {
        const row: any[] = [];
        for (let j = 0; j < columns.length; j++) {
          row.push(result.getChildAt(j)?.get(i));
        }
        rows.push(row);
      }

      return {
        columns,
        rows,
        rowCount: result.numRows,
      };
    } catch (err: any) {
      return {
        columns: [],
        rows: [],
        rowCount: 0,
        error: err.message || 'Query failed',
      };
    }
  }

  return {
    get isInitialized() {
      return initialized;
    },

    async initialize(containerEl: HTMLElement): Promise<void> {
      if (initialized) {
        console.warn('[DataNotebook] Shell already initialized');
        return;
      }

      try {
        // Render UI first (shows loading state)
        renderUI(containerEl);
        appendOutput('Initializing DuckDB...', 'result');

        // Resolve asset URLs at runtime (files are copied alongside the bundle)
        const baseUrl = getAssetBaseUrl();
        const wasmUrl = baseUrl + 'duckdb-eh.wasm';
        const workerUrl = baseUrl + 'duckdb-browser-eh.worker.js';

        console.log('[DataNotebook] Loading DuckDB from:', { wasmUrl, workerUrl });

        // Create worker and database
        worker = new Worker(workerUrl);
        const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
        db = new duckdb.AsyncDuckDB(logger, worker);

        // Instantiate the database
        await db.instantiate(wasmUrl);

        // Create connection
        conn = await db.connect();

        initialized = true;
        appendOutput('DuckDB ready! Try: SELECT * FROM duckdb_functions() LIMIT 5;', 'success');
        console.log('[DataNotebook] DuckDB REPL initialized');

        // Focus input
        const input = containerEl.querySelector('#dn-repl-input') as HTMLInputElement;
        input?.focus();
      } catch (error: any) {
        console.error('[DataNotebook] Failed to initialize REPL:', error);
        appendOutput(`Failed to initialize: ${error.message}`, 'error');
        throw error;
      }
    },

    getDatabase(): duckdb.AsyncDuckDB | null {
      return db;
    },

    executeQuery,

    async destroy(): Promise<void> {
      if (conn) {
        await conn.close();
        conn = null;
      }
      if (db) {
        await db.terminate();
        db = null;
      }
      if (worker) {
        worker.terminate();
        worker = null;
      }
      initialized = false;
      container = null;
      console.log('[DataNotebook] DuckDB REPL destroyed');
    },
  };
}
